import React, { useEffect, useState } from "react";
import { PrimaryButton } from "../UI";
import { useUserContext } from "../../contexts/UserContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SuccessAlert from "../UI/Alert/SuccessAlert";
import Label from "../UI/Label";
import Loader from "../UI/Loader/Loader";
import { Field, Form, Formik } from "formik";
import { GithubExperienceSyncSchema } from "../../schemas";
import Dropdown from "../UI/Dropdown/Dropdown";
import { getSavedTasks, saveTask, updateTask } from "../../utils/APIs";
import { useContentfulContext } from "../../contexts/ContentfulContext";
import Tooltip from "../UI/Tooltip";

const GithubExperienceSynchronization = () => {
  const { userData } = useUserContext();
  const { content } = useContentfulContext();

  const { pathname } = useLocation();
  const { saved_task_id } = useParams();
  const navigate = useNavigate();

  const [showSuccess, setShowSuccess] = useState("");
  const [showError, setShowError] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState();

  useEffect(() => {
    if (saved_task_id) getSavedTasksDataById();
  }, []);

  const getSavedTasksDataById = async () => {
    try {
      setLoading(true);
      const result = await getSavedTasks(
        userData?.token,
        userData?.selectedOrganization?.org_token,
        saved_task_id
      );
      setData(result.data[0]);
    } catch (error) {
      console.log("error getSavedTasksDataById: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      setShowError("");
      let result;

      if (saved_task_id) {
        const data = {
          status: values?.status,
          github_integration_id: values?.github_integration_id,
          is_recurring : true
        };
        result = await updateTask(userData.token, userData.selectedOrganization.org_token, {
          id: saved_task_id,
          data,
        });
      } else {
        const data = {
          feature_id: content.products.github.id,
          saved_task_name: values.saved_task_name,
          function_id: pathname.split("/").pop(),
          github_integration_id: values?.github_integration_id,
          status: values.status,
          is_recurring : true
        };
        result = await saveTask(
          userData.token,
          userData.selectedOrganization.org_token,
          data
        );
      }

      setShowSuccess(result.message);
      navigate("/functions/saved-tasks");
    } catch (error) {
      setShowError(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
    } finally {
      setLoading(false);
    }
  };

  // Transform GitHub integrations for dropdown
  const githubOptions =
    userData?.githubIntegrations?.map((integration) => ({
      value: integration._id,
      name: integration.name,
    })) || [];

  return (
    <div className="w-full max-w-screen px-4 md:px-8 my-6">
      {loading && <Loader />}
      {showSuccess && (
        <SuccessAlert disappearTime={3000}>
          {showSuccess}
        </SuccessAlert>
      )}
      <div className="flex flex-col w-full lg:w-1/2 justify-center items-start">
        <div className="space-y-4 md:space-y-6 w-full">
          <Formik
            enableReinitialize
            initialValues={{
              saved_task_name: data?.saved_task_name || "",
              status: data?.status || "",
              github_integration_id: data?.github_integration_id?._id || "",
            }}
            validationSchema={() =>
              GithubExperienceSyncSchema(content.GITHUB_EXPERIENCE_SYNC_CONTENT)
            }
            onSubmit={handleSubmit}
          >
            {({ errors, touched, setFieldValue, values }) => (
              <Form>
                <h2 className="text-xl font-bold leading-tight tracking-tight text-black text-left mb-4">
                  {content.GITHUB_EXPERIENCE_SYNC_CONTENT.title}
                </h2>
                <p className="mb-6">{content.GITHUB_EXPERIENCE_SYNC_CONTENT.description}</p>

                {/* Row 1: Task Name & GitHub Integration */}
                <div className="flex flex-col md:flex-row gap-8 mb-4">
                  <div className="w-full">
                    <div className="relative">
                      <Field
                        type="text"
                        id="saved_task_name"
                        name="saved_task_name"
                        className="block px-2.5 pb-2.5 pt-2.5 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-2 appearance-none focus:outline-none focus:ring-1 focus:ring-primary-2 ring-primary peer"
                        placeholder=" "
                        disabled={saved_task_id}
                      />
                      <Label labelFor="saved_task_name">Task Name</Label>
                      {/* Your updated Portal Tooltip */}
                      <Tooltip
                        content="Enter a descriptive name for this sync task"
                        class_name="-right-6 top-1/2"
                      />
                    </div>
                    {errors.saved_task_name && touched.saved_task_name && (
                      <p className="mt-2 text-sm text-red-5">{errors.saved_task_name}</p>
                    )}
                  </div>

                  <div className="w-full">
                    <div className="relative w-full">
                      <Dropdown
                        width={100}
                        isWidthInPercentage={true}
                        initialValue={githubOptions.find(
                          (x) => x.value === values.github_integration_id
                        )}
                        items={githubOptions}
                        callback={(val) => setFieldValue("github_integration_id", val.value)}
                      />
                      <Label labelFor="github_integration_id">GitHub Integration</Label>
                      <Tooltip
                        content="Select the GitHub integration to use for syncing"
                        class_name="-right-6 top-1/2"
                      />
                    </div>
                    {errors.github_integration_id && touched.github_integration_id && (
                      <p className="mt-2 text-sm text-red-5">{errors.github_integration_id}</p>
                    )}
                  </div>
                </div>

                {/* Row 2: Status (and potential future fields) */}
                <div className="flex flex-col md:flex-row gap-8 mb-4">
                  <div className="w-full md:w-1/2">
                    <div className="relative">
                      <Dropdown
                        width={100}
                        isWidthInPercentage={true}
                        initialValue={content.LAMBDA_STATUS_VALUES.find(
                          (x) => x.value == values.status
                        )}
                        items={content.LAMBDA_STATUS_VALUES}
                        callback={(val) => setFieldValue("status", val.value)}
                      />
                      <Label labelFor="status">Status</Label>
                      {/* Added tooltip here for visual symmetry if needed */}
                      <Tooltip
                        content="Set the current status of this task"
                        class_name="-right-6 top-1/2"
                      />
                    </div>
                    {errors.status && touched.status && (
                      <p className="mt-2 text-sm text-red-5">{errors.status}</p>
                    )}
                  </div>

                  {/* Empty spacer div to maintain 50/50 alignment if status is alone on the row */}
                  <div className="hidden md:block w-full"></div>
                </div>

                {showError && <p className="my-2 text-sm text-red-5">{showError}</p>}

                <div className="flex justify-end mt-6">
                  <PrimaryButton type="submit">
                    {content.GITHUB_EXPERIENCE_SYNC_CONTENT.submit_cta || "Save Task"}
                  </PrimaryButton>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default GithubExperienceSynchronization;
